import React, { useEffect, useState } from "react";
import { FaSearch, FaBriefcase, FaArrowRight, FaRocket } from "react-icons/fa";
import { getSummary } from "../../apis/UserApis";
import { useTheme } from "../Theme/ThemeContext";

export default function CallToAction() {
  const [summary, setSummary] = useState(null);
  const { currentTheme } = useTheme();

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const data = await getSummary();
        if (!isMounted) return;
        setSummary(data);
      } catch (error) {
        // Keep UI stable with fallback values
        if (!isMounted) return;
        setSummary(null);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const totalJobs = typeof summary?.totalJobs === "number" ? summary.totalJobs : 0;
  const totalApplications =
    typeof summary?.totalApplications === "number" ? summary.totalApplications : 0;
  const totalRegisteredUsers =
    typeof summary?.totalRegisteredUsers === "number"
      ? summary.totalRegisteredUsers
      : 0;

  return (
    <section className="py-24 relative overflow-hidden" style={{ background: `linear-gradient(135deg, rgb(var(--color-primary)) 0%, rgb(var(--color-secondary)) 50%, rgb(var(--color-accent)) 100%)` }}>
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: `rgba(var(--color-primary), 0.3)` }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ background: `rgba(var(--color-accent), 0.3)` }}></div>
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/5 w-2 h-2 bg-white/30 rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
        <div className="absolute top-3/4 right-1/4 w-1.5 h-1.5 bg-amber-400/40 rounded-full animate-ping" style={{ animationDuration: '2.5s' }}></div>
        <div className="absolute top-1/2 left-3/4 w-1 h-1 bg-white/40 rounded-full animate-ping" style={{ animationDuration: '4s' }}></div>
      </div>

      <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-8">
          <FaRocket className="w-4 h-4 text-yellow-400 animate-bounce" />
          <span className="text-white/90 text-sm font-medium">Start Your Journey Today</span>
        </div>

        {/* Heading */}
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Ready to Transform Your
          <span className="block mt-2 bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
            Career Journey?
          </span>
        </h2>

        {/* Subtext */}
        <p className="text-xl text-white/80 mb-10 leading-relaxed max-w-2xl mx-auto">
          Explore top job, internship, and referral opportunities to kickstart
          or accelerate your career. Your dream role is just one click away.
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
          <a href="/jobs">
            <button
              type="button"
              className="group inline-flex items-center justify-center gap-2 font-bold rounded-xl bg-white text-accent-700 hover:bg-accent-50 shadow-2xl shadow-black/20 px-8 py-4 text-lg w-full sm:w-auto transition-all duration-300 hover:scale-105 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]"
            >
              <FaBriefcase className="w-5 h-5" />
              Browse Jobs & Internships
              <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </a>

          <a href="/referrals">
            <button
              type="button"
              className="group inline-flex items-center justify-center gap-2 font-semibold rounded-xl border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 backdrop-blur-md px-8 py-4 text-lg w-full sm:w-auto transition-all duration-300"
            >
              <FaSearch className="w-4 h-4" />
              Explore Referrals
            </button>
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          <Stat target={totalJobs} suffix="+" label="Total Jobs" />
          <Stat target={totalApplications} suffix="+" label="Total Applications" />
          <Stat target={totalRegisteredUsers} suffix="+" label="Registered Users" />
        </div>
      </div>
    </section>
  );
}

// Small reusable stat component
function Stat({ target, label, suffix = "" }) {
  // Longer duration for larger numbers so the animation feels smoother.
  const durationMs = Math.min(4500, Math.max(2000, Math.round(900 + (Number(target) || 0) * 2.2)));
  const animatedValue = useCountUp(target, { durationMs, delayMs: 200 });
  const formatted = animatedValue.toLocaleString();

  return (
    <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
      <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
        {formatted}
        {suffix}
      </div>
      <div className="text-white/70 text-sm">{label}</div>
    </div>
  );
}

function useCountUp(target, { durationMs = 2000, delayMs = 0 } = {}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reducedMotion) {
      setValue(Math.max(0, Number(target) || 0));
      return;
    }

    const safeTarget = Math.max(0, Math.floor(Number(target) || 0));
    const startValue = 0;
    if (safeTarget === 0) {
      setValue(0);
      return;
    }

    let rafId = 0;
    let timeoutId;

    const tick = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(startValue + (safeTarget - startValue) * eased);
      setValue(next);
      if (t < 1) rafId = requestAnimationFrame(tick);
    };

    let startTime = 0;
    const start = () => {
      startTime = performance.now();
      rafId = requestAnimationFrame(tick);
    };

    if (delayMs > 0) {
      timeoutId = setTimeout(start, delayMs);
    } else {
      start();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [target, durationMs, delayMs]);

  return value;
}
